import { useState } from "react";
import { motion } from "framer-motion";
import { ShareNetwork, Copy, CheckCircle, Gift } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { api, formatApiError } from "../lib/api";

export default function Referral() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [link, setLink] = useState(null);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/referrals/join", { name, email });
      setLink(`${window.location.origin}/?ref=${data.code}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select the link and copy it manually.");
    }
  };

  const inputCls =
    "w-full bg-transparent border border-primary-foreground/25 rounded-sm px-4 py-3.5 text-sm font-medium text-primary-foreground placeholder:text-primary-foreground/35 focus:outline-none focus:border-accent transition-colors duration-300";

  return (
    <section id="refer" data-testid="referral-section" className="py-24 md:py-36 bg-primary text-primary-foreground">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <Eyebrow className="!text-accent">Refer &amp; Reward</Eyebrow>
          <h2 className="font-serif tracking-tight text-4xl sm:text-5xl mt-6 leading-[1.08]" data-testid="referral-heading">
            Know an owner <em className="text-accent">who's stuck?</em>
          </h2>
          <p className="mt-6 text-primary-foreground/70 max-w-xl leading-relaxed">
            Every owner knows another owner. Get your personal link below — when someone you send
            books a consultation, they save $39, and you earn a free month of Compass Membership
            for each one. No limits, no expiration.
          </p>
          <ul className="mt-8 space-y-3 max-w-md">
            {[
              "They get $39 off their Clarity Session",
              "You get a free membership month per referral",
              "Rewards tracked automatically in our records",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm font-medium text-primary-foreground/80">
                <Gift size={17} weight="duotone" className="text-accent shrink-0 mt-0.5" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="bg-primary-foreground/5 border border-primary-foreground/15 rounded-sm p-8 md:p-12" data-testid="referral-card">
            {!link ? (
              <form onSubmit={generate} className="space-y-4" data-testid="referral-form">
                <p className="font-serif text-2xl tracking-tight">Get your personal link</p>
                <input data-testid="referral-name" required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                <input data-testid="referral-email" required type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                <button
                  data-testid="referral-generate-button"
                  type="submit"
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-2 bg-accent text-accent-foreground px-7 py-4 rounded-full font-semibold text-sm hover:bg-background hover:text-primary disabled:opacity-50 transition-colors duration-300"
                >
                  <ShareNetwork size={16} weight="bold" />
                  {sending ? "Creating…" : "Create my referral link"}
                </button>
              </form>
            ) : (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="referral-result">
                <p className="font-serif text-2xl tracking-tight">Your link is ready</p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <code data-testid="referral-link" className="flex-1 bg-primary border border-primary-foreground/20 rounded-sm px-4 py-3.5 text-sm text-accent font-semibold truncate">
                    {link}
                  </code>
                  <button
                    data-testid="referral-copy-button"
                    onClick={copy}
                    className="flex items-center justify-center gap-2 bg-accent text-accent-foreground px-6 py-3.5 rounded-full font-semibold text-sm hover:bg-background hover:text-primary transition-colors duration-300 shrink-0"
                  >
                    {copied ? <CheckCircle size={16} weight="fill" /> : <Copy size={16} weight="bold" />}
                    {copied ? "Copied" : "Copy link"}
                  </button>
                </div>
                <p className="mt-5 text-xs text-primary-foreground/50 leading-relaxed">
                  Share it anywhere — email, owner groups, a text to that friend from the resort.
                  When someone arrives through it and reaches out, we credit you automatically.
                </p>
              </motion.div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
